import { useState, useCallback, useEffect, useMemo } from 'react';
import { useAuthStore } from '@/store/authStore';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Cctv, Folder, HelpCircle, Plug, ShieldAlert, Sparkles } from 'lucide-react';
import { Building2, MonitorCog, Users, CheckCircle } from 'lucide-react';
import { useRouteAccess, Roles } from './Roles/useRouteAccess';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { SimpleTooltip } from '@/components/ui/tooltip';
import { FeatureFlags } from '@/lib/feature-flag';
import { OnboardingWelcomeModal } from '@/components/onboarding/OnboardingWelcomeModal';
import { HELP_CENTER_LINKS } from '@/lib/onboardingTours/helpCenterLinks';
import { AnnouncementBannerCenter, WhatsNewLink } from '@/features/announcements';

const Home = () => {
  const ANALOG_THRESHOLD = 0.5;
  const GAMEPAD_POLL_RATE = 100;
  const auth = useAuthStore();
  const navigate = useNavigate();
  const watchfulOnlyFlag = useFeatureFlagEnabled('watchful-only');
  const monitoringOverviewFlag = useFeatureFlagEnabled('monitoring-overview');
  const ultraTopSecretFlag = useFeatureFlagEnabled('ultra-top-secret');

  const menuItems = useMemo(
    () => [
      {
        title: 'Monitoring',
        description: 'Start protecting clients',
        icon: <Cctv className="h-8 w-8 text-lime" />,
        href: '/monitor',
        route: '/monitor',
      },

      {
        title: 'Customers',
        description: 'View and Manage customers',
        icon: <Folder className="h-8 w-8 text-lime" />,
        href: '/customers',
        route: '/customers',
        flag: FeatureFlags.AdminPortal,
      },
      {
        title: 'Sites',
        description: 'View and Manage sites',
        icon: <Building2 className="h-8 w-8 text-lime" />,
        href: '/sites',
        route: '/sites',
        flag: FeatureFlags.AdminPortal,
      },
      {
        title: 'Devices',
        description: 'View and Manage devices',
        icon: <Plug className="h-8 w-8 text-lime" />,
        href: '/devices',
        route: '/devices',
        flag: FeatureFlags.AdminPortal,
      },
      {
        title: 'Ingestion',
        description: 'Event pipeline and processing',
        icon: <MonitorCog className="h-8 w-8 text-lime" />,
        href: '/ingestion',
        route: '/ingestion',
        flag: FeatureFlags.AdminPortal,
      },
      {
        title: 'Alerts',
        description: 'View and Manage alerts',
        icon: <ShieldAlert className="h-8 w-8 text-lime" />,
        href: '/alerts',
        route: '/alerts',
        flag: FeatureFlags.AdminPortal,
      },
      {
        title: 'Users',
        description: 'View and Manage users',
        icon: <Users className="h-8 w-8 text-lime" />,
        href: '/users',
        route: '/users',
        flag: FeatureFlags.AdminPortal,
      },
      {
        title: 'Support',
        description: 'For instructions and general support',
        icon: <HelpCircle className="h-8 w-8 text-lime" />,
        href: HELP_CENTER_LINKS.supportHomepage,
      },
    ],
    []
  );

  const { hasAccessToRoute, hasRoleAccess } = useRouteAccess();
  const isAdmin =
    hasRoleAccess(Roles.SuperAdmin) ||
    hasRoleAccess(Roles.ProjectAdmin) ||
    hasRoleAccess(Roles.IntegratorAdmin) ||
    hasRoleAccess(Roles.CompanyAdmin);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const handleInput = useCallback(
    (event?: KeyboardEvent) => {
      if (
        document.activeElement?.tagName === 'INPUT' ||
        document.activeElement?.tagName === 'TEXTAREA'
      ) {
        return;
      }

      const key = event?.key;
      const gamepad = navigator.getGamepads?.()?.[0];
      const dpadLeft = gamepad?.buttons[14]?.pressed;
      const dpadRight = gamepad?.buttons[15]?.pressed;
      const analogX = gamepad?.axes[0] || 0;
      const isEnter = key === 'Enter' || gamepad?.buttons[0]?.pressed; // A button

      const isLeft = key === 'ArrowLeft' || dpadLeft || analogX < -ANALOG_THRESHOLD;
      const isRight = key === 'ArrowRight' || dpadRight || analogX > ANALOG_THRESHOLD;

      if (isLeft) {
        event?.preventDefault?.();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
      } else if (isRight) {
        event?.preventDefault?.();
        setSelectedIndex((prev) => (prev < menuItems.length - 1 ? prev + 1 : prev));
      } else if (isEnter) {
        event?.preventDefault?.();
        const selectedItem = menuItems[selectedIndex];
        if (selectedItem.href.startsWith('/')) {
          navigate(selectedItem.href);
        } else if (selectedItem.href !== '#') {
          window.open(selectedItem.href, '_blank');
        }
      }
    },
    [menuItems, selectedIndex, navigate]
  );

  // Handle keyboard events
  useEffect(() => {
    window.addEventListener('keydown', handleInput);
    return () => {
      window.removeEventListener('keydown', handleInput);
    };
  }, [handleInput]);

  // Poll gamepad state
  useEffect(() => {
    const interval = setInterval(() => {
      handleInput();
    }, GAMEPAD_POLL_RATE);

    return () => {
      clearInterval(interval);
    };
  }, [handleInput]);

  // TODO Confirm roles

  if (auth.isCustomerUser) {
    return <Navigate to="/404" replace />;
  }

  return (
    <>
    <AnnouncementBannerCenter />
    <section
      className="relative mx-auto my-16 max-w-[1600px] px-4 text-white md:px-6 lg:px-8"
      data-onboarding="home-welcome"
    >
      {/* Account Suspension Warning */}

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-3 flex items-center justify-between border-b py-6">
          <h1 className="text-4xl font-semibold max-2xl:text-2xl">
            Welcome back, {auth.user?.firstName}
          </h1>
          <WhatsNewLink />
        </div>
      </div>
      <div
        className="mt-6 flex flex-row flex-wrap gap-4 overflow-x-auto pb-2"
        data-onboarding="home-options"
      >
        {menuItems.map((item, index) => {
          if (item.flag === FeatureFlags.WatchfulOnly && !watchfulOnlyFlag) return <></>;
          if (item.flag === FeatureFlags.MonitoringOverview && !monitoringOverviewFlag)
            return <></>;
          if (item.flag === FeatureFlags.UltraTopSecret && !ultraTopSecretFlag) return <></>;

          // Skip if user doesn't have access to the route
          if (item.route && !hasAccessToRoute(item.route)) return <></>;

          // Skip admin-only items (Sites, Devices, System) if user is not an admin
          if (
            !isAdmin &&
            (item.title === 'Sites' || item.title === 'Devices' || item.title === 'System')
          ) {
            return <></>;
          }

          return (
            <SimpleTooltip key={index} content={item.description}>
              <a
                className={`flex flex-1 flex-col items-center justify-center gap-3 rounded-lg border bg-card2 p-4 hover:bg-[#2E3835] max-2xl:flex-1 max-xl:aspect-auto ${
                  index === selectedIndex ? 'ring-lime' : ''
                }`}
                href={item.href}
                target={item.href.startsWith('/') ? undefined : '_blank'}
                rel={item.href.startsWith('/') ? undefined : 'noopener noreferrer'}
                data-onboarding={item.title === 'Monitoring' ? 'monitoring-menu' : undefined}
              >
                {item.icon}
                <h2 className="mt-2 text-center text-sm font-semibold text-lime">{item.title}</h2>
              </a>
            </SimpleTooltip>
          );
        })}
        {watchfulOnlyFlag ? (
          <SimpleTooltip content="Internal Products & Demos">
            <a
              className={`flex flex-1 flex-col items-center justify-center gap-3 rounded-lg border bg-card2 p-4 hover:bg-[#2E3835] max-2xl:flex-1 max-xl:aspect-auto`}
              href="https://lab.watchful.co.nz"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Sparkles className="h-8 w-8 text-lime" />
              <h2 className="mt-2 text-center text-sm font-semibold text-lime">Lab</h2>
            </a>
          </SimpleTooltip>
        ) : null}
      </div>
      {watchfulOnlyFlag ? (
        <section className="mt-4 grid gap-4 max-2xl:grid-cols-1">
          <div className="rounded-lg border border-[#2E3835] bg-card2">
            <div className="flex items-center justify-between border-b border-[#2E3835]">
              <h2 className="p-4 text-lg font-semibold">System health</h2>{' '}
              <Link className="p-4 font-semibold text-[#B7D257]" to="/ingestion">
                See More
              </Link>
            </div>
            <div className="flex h-80 flex-col items-center justify-center p-6 text-center">
              <CheckCircle className="mb-4 h-10 w-10" />
              <h2 className="text-xl font-semibold">System Healthy</h2>
            </div>
          </div>
          {/* <div className="rounded-lg border border-[#2E3835] bg-card2 max-2xl:h-[30rem]">
            <div className="flex items-center justify-between border-b border-[#2E3835]">
              <h2 className="p-4 text-lg font-semibold">Alert Volume</h2>{' '}
              <Link className="p-4 font-semibold text-[#B7D257]" to="/ingestion">
                See More
              </Link>
            </div>
            <div className="h-64">
              <div className="flex items-center justify-between border-b border-[#2E3835] bg-card2">
                <div className="flex flex-1 items-center p-4">
                  <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-full bg-[#B7D257]/10">
                    <CheckCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-lg font-semibold">1,240</div>
                    <div className="text-sm text-gray-400">Actioned alerts this month</div>
                  </div>
                </div>
                <div className="flex flex-1 items-center border-l p-4">
                  <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-full bg-[#2E3835]/30">
                    <svg
                      className="h-5 w-5 text-gray-300"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M9 14l6-6" />
                      <circle cx="9" cy="9" r="7" />
                      <path d="M9 9h.01" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-gray-300">91,240</div>
                    <div className="text-sm text-gray-400">Discarded by Quill</div>
                  </div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height="100%" className="">
                <BarChart
                  data={alertVolumeData}
                  margin={{ top: 20, right: 10, left: 0, bottom: 20 }}
                  barSize={12}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#2E3835" vertical={false} />
                  <XAxis
                    dataKey="name"
                    stroke="#666"
                    tick={{ fill: '#999' }}
                    axisLine={{ stroke: '#2E3835' }}
                    tickLine={{ stroke: '#2E3835' }}
                    padding={{ left: 0, right: 0 }}
                  />
                  <YAxis
                    stroke="#666"
                    tick={{ fill: '#999' }}
                    axisLine={{ stroke: '#2E3835' }}
                    tickLine={{ stroke: '#2E3835' }}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(183, 210, 87, 0.1)' }}
                    contentStyle={{
                      backgroundColor: '#111',
                      border: '1px solid #2E3835',
                      borderRadius: '2px',
                      color: '#fff',
                    }}
                    formatter={(value, name) => {
                      if (name === 'projected') return [`${value} (projected)`, 'Alerts'];
                      return [`${value}`, 'Alerts'];
                    }}
                  />
                  <ReferenceLine x="17" stroke="#666" strokeDasharray="3 3" />
                  <Bar dataKey="value" fill="#E5DBC1" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="projected" fill="rgba(229,219,193,0.2)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div> */}
        </section>
      ) : (
        <></>
      )}

      {/* Onboarding Welcome Modal for first-time users */}
      <OnboardingWelcomeModal />
    </section></>
  );
};

export default Home;
